<template>
    <BaseInput
        v-bind="$attrs"
        :model-value="type === 'range' ? (date?.start && date?.end ? date : null) : (date || null)"
        :label="$attrs.label"
        :name="$attrs.name || $attrs.label">
        <div>
            <div v-if="disabled">
                <div
                    class="flex justify-start items-center gap-x-4
                        rounded-md border-0 ring-1 ring-inset placeholder:text-gray-400
                        appearance-none w-full py-2 px-2 leading-tight
                        focus:ring-primary focus:ring-2 focus:ring-inset sm:text-sm focus:outline-none focus:shadow-outline
                        bg-gray-50 text-gray-500 ring-gray-200 dark:ring-gray-200 cursor-not-allowed
                        dark:bg-gray-700 shadow-none dark:ring-gray-400
                        dark:text-gray-400 dark:placeholder:text-gray-200">
                    <Icon name="calendar" />
                    <span>{{ dateString }}</span>
                </div>
            </div>

            <Menu
                v-else
                as="div"
                class="relative inline-block text-left w-full">
                <div class="relative">
                    <BaseInput
                        v-model="dateString"
                        input-classes="pl-8"
                        :mask="{mask: inputMask, eager: true}"
                        :name="($attrs.name || $attrs.label) + 'masked-input'">
                        <template #inputIcon>
                            <div
                                class="absolute inset-y-0 flex items-center pr-3"
                                :class="{'cursor-pointer': !hidePopover}">
                                <MenuButton
                                    :id="$attrs.label"
                                    class="flex items-center">
                                    <Icon
                                        name="calendar"
                                        class="ml-2" />
                                </MenuButton>
                            </div>
                        </template>
                    </BaseInput>


                    <div
                        v-if="clearable && modelValue"
                        class="absolute cursor-pointer right-0 top-0 bottom-0 flex px-2 items-center"
                        @click="clearDate">
                        <Icon
                            name="x-circle"
                            color="gray-400" />
                    </div>
                </div>

                <Transition
                    v-if="!hidePopover"
                    enter-active-class="transition ease-out duration-100"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95">
                    <MenuItems
                        :class="'absolute ' + anchorPoint + '-0 z-10 mt-2 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'">
                        <MenuItem v-slot="{close}">
                            <BaseCalendar
                                v-if="type === 'range'"
                                v-model.range="date"
                                :columns="columns"
                                :mode="mode"
                                :is-required="isRequired"
                                :disabled-dates="disabledDates"
                                :min-date="minDate"
                                :max-date="maxDate"
                                :trim-weeks="trimWeeks"
                                @update:model-value="mode === 'date' && close()"
                                @click.prevent="null" />
                            <BaseCalendar
                                v-else
                                v-model="date"
                                :columns="columns"
                                :mode="mode"
                                :is-required="isRequired"
                                :disabled-dates="disabledDates"
                                :min-date="minDate"
                                :max-date="maxDate"
                                :trim-weeks="trimWeeks"
                                @update:model-value="mode === 'date' && close()"
                                @click.prevent="null" />
                        </MenuItem>
                    </MenuItems>
                </Transition>
            </Menu>
        </div>
    </BaseInput>
</template>

<script setup>
const dayjs = useDayjs()

const props = defineProps({
    modelValue: {
        type: [String, Date, Object, null],
        default: null
    },
    showLabel: {
        type: Boolean,
        default: true
    },
    format: {
        type: String,
        default: 'dateShort'
    },
    type: {
        type: String,
        default: 'single',
        validator: (value) => ['single', 'range'].includes(value)
    },
    columns: {
        type: Number,
        default: 1
    },
    trimWeeks: {
        type: Boolean,
        default: true
    },
    mode: {
        type: String,
        default: 'date',
        validation: (value) => ['date', 'dateTime', 'time'].includes(value)
    },
    isRequired: {
        type: Boolean,
        default: false,
        description: 'Only prevents the user from deselecting a date once it has been set'
    },
    disabledDates: {
        type: Array,
        default: () => []
    },
    minDate: {
        type: [String, Number, Date, Object, null],
        default: null
    },
    maxDate: {
        type: [String, Number, Date, Object, null],
        default: null
    },
    disabled: {
        type: Boolean,
        default: false
    },
    anchorPoint: {
        type: String,
        default: 'left',
        validator: (value) => ['left', 'right'].includes(value)
    },
    clearable: {
        type: Boolean,
        default: false
    },
    hidePopover: {
        type: Boolean,
        default: false
    }
})

const emits = defineEmits(['update:modelValue'])

/**
 * Localized string representation of date / time, editable by user directly
 *
 * This is always in local time and formatted according to the dateFormat computed property.
 * It is kept in sync with the actual date value in UTC via watchers.
 */
const dateString = ref('')

/**
 * The actual date / time value as string in UTC Date Object format
 *
 * We expect the modelValue to come in as a UTC date string (e.g. "2024-01-31" or "2024-01-31 14:30")
 * or an ISO string (e.g. "2024-01-31T14:30:00Z")
 * or as an object with start and end properties for range selection in the same formats
 */
const date = computed({
    get: () => fromDate(props.modelValue, (date) => parseModelForCentralUsage(date).toDate()),
    set: (value) => emits('update:modelValue', valueToString(value))
})

/**
 * Parses the model value date string to a dayjs object in local time for central usage
 *
 * We can only be sure that dateTime values are in UTC, date values are always treated as local time, because they are time zone agnostic
 * and time values are treated as local time as well, because they are only relevant in the local context
 */
function parseModelForCentralUsage(date) {
    if (props.mode === 'time') return dayjs(date, 'HH:mm:ss')
    if (props.mode === 'dateTime') return dayjs.utc(date)
    return dayjs(date)
}

// allow user input by parsing the input string as dates
const inputMask = computed(() => {
    let baseMask = '##.##.####'
    if (props.mode === 'time') {
        baseMask = '##:##'
    } else if (props.mode === 'dateTime') {
        baseMask = '##.##.#### ##:##'
    }

    if (props.type === 'range') {
        return baseMask + ' - ' + baseMask
    }

    return baseMask
})

// The displayed and user editable format of the date / time
const dateFormat = computed(() => {
    if (props.mode === 'time') {
        return 'HH:mm'
    } else if (props.mode === 'dateTime') {
        return 'DD.MM.YYYY HH:mm'
    }

    return 'DD.MM.YYYY'
})

/**
 * The parsed date / time from the user input string in local time
 *
 * This will ensure, that the input string is only considered valid if it matches the input mask and can be parsed to a valid date.
 * If the type is 'range', both dates need to be valid.
 */
const validDateFromString = computed(() => {
    if (inputMask.value?.length !== dateString.value.length) return null
    if (props.type === 'single') {
        const unsureDate = dayjs(dateString.value, dateFormat.value)
        return unsureDate.isValid() ? unsureDate : null
    }
    const [start, end] = dateString.value?.split(' - ') || [null, null]
    const rangeDate = fromDate({start, end}, date => dayjs(date, dateFormat.value))
    return rangeDate.start?.isValid() && rangeDate.end?.isValid() ? rangeDate : {start: null, end: null}
})

watch(() => date.value, (value) => {
    updateLocaleDateStringFromModelValueIfNecessary(value)
}, {immediate: true, deep: true})

watch(() => validDateFromString.value, (value) => {
    updateModelValueFromLocaleDateStringIfNecessary(value)
})

/**
 * Updates the locale date string from the model value if necessary to avoid infinite loops
 *
 * This is called whenever the model value changes. It will only update the date string if it is different from the current value.
 * This ensures that the user can edit the date string without it being overwritten by the model value.
 * Importantly, if the picker is in date mode, the time portion of the model value is ignored when updating the date string.
 * This prevents issues where the time portion could cause the date string to be updated unnecessarily.
 */
function updateLocaleDateStringFromModelValueIfNecessary(value) {
    if (props.type === 'range') {
        if (value?.start && value?.end) {
            const newDateString = dayjs(value.start).format(dateFormat.value) + ' - ' + dayjs(value.end).format(dateFormat.value)
            // only cause update if necessary to avoid infinite loops
            if (!(validDateFromString.value?.start && !validDateFromString.value?.end) || !dayjs(value.start).isSame(validDateFromString.value?.start) || !dayjs(value.end).isSame(validDateFromString.value?.end) || dateString.value !== newDateString) {
                dateString.value = newDateString
            }
        } else if (validDateFromString.value?.start || validDateFromString.value?.end) {
            dateString.value = ''
        }
    } else {
        if (value) {
            const newDateString = dayjs(value).format(dateFormat.value)
            // only cause update if necessary to avoid infinite loops
            if (!validDateFromString.value || !dayjs(value).isSame(validDateFromString.value) || dateString.value !== newDateString) {
                dateString.value = newDateString
            }
        } else if (validDateFromString.value) {
            dateString.value = ''
        }
    }
}

/**
 * Updates the model value from the locale date string if necessary to avoid infinite loops
 *
 * This is called whenever the locale date string changes. It will only update the model value if it is different from the current value.
 * This ensures that the user can edit the date string without it being overwritten by the model value.
 * Importantly, if the picker is in date mode, the time portion of the model value is ignored when updating the model value.
 * This prevents issues where the time portion could cause the model value to be updated unnecessarily.
 */
function updateModelValueFromLocaleDateStringIfNecessary(value) {
    if (props.type === 'range') {
        if (value?.start && value?.end) {
            if (!(date.value.start && date.value.end) || !dayjs(value.start).isSame(date.value.start) || !dayjs(value.end).isSame(date.value.end)) {
                date.value = value
            }
        } else if (date.value?.start || date.value?.end) {
            date.value = {start: null, end: null}
        }
    } else {
        if (value) {
            if (!date.value || !dayjs(value).isSame(date.value)) {
                date.value = value
            }
        } else if (date.value) {
            date.value = null
        }
    }
}

// Formats any date object that is set as the model value to a string in the correct format for the model
function valueToString(value) {
    if (value === null) return null
    const date = fromDate(value, date => props.mode === 'dateTime' ? dayjs.utc(date) : dayjs(date))
    const format = props.mode === 'date' ? 'YYYY-MM-DD' : (props.mode === 'time' ? 'HH:mm:ss' : 'YYYY-MM-DD HH:mm')
    return fromDate(date, date => date.format(format))
}

// helper to apply a formatter function to the date or both dates in a range
function fromDate(date, formatter = _ => _) {
    if (!date) return null
    if (props.type === 'range') {
        return {
            start: date.start ? formatter(date.start) : null,
            end: date.end ? formatter(date.end) : null
        }
    }
    return formatter(date)
}

function clearDate(event) {
    event.preventDefault()
    date.value = props.type === 'range' ? {start: null, end: null} : null
}
</script>
